import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

/**
 * Top-level force-password-change redirect.
 *
 * Sits OUTSIDE the lazy-loaded ``<Suspense>`` route boundary in
 * ``App.jsx`` so the redirect fires the moment ``AuthContext``
 * reports ``forcePasswordChange=true``, regardless of whether the
 * currently matched route's element is still loading its lazy chunk.
 *
 * Background: earlier attempts fixed the
 * "Force password change redirects to change-password page" E2E
 * scenario from the test side (helper polling, cookie clear, backend
 * allowlist, page.goto workaround). The actual race lives in the SPA:
 *
 *   1. ``Login.tsx`` runs ``await switchTenant()`` which calls
 *      ``setUser({...force_password_change:true})``.
 *   2. ``Login.tsx`` immediately calls ``navigate('/home')``.
 *   3. The router begins resolving the lazy ``<Index/>`` chunk;
 *      ``<Suspense fallback={<PageLoading/>}/>`` renders while the
 *      chunk loads.
 *   4. ``AppRequired`` (which would emit the redirect) is wrapped
 *      around ``<Index/>`` and so cannot run until the lazy chunk
 *      resolves — by which time CI has timed out.
 *
 * This gate component renders nothing, but its effect dispatches the
 * navigation as soon as ``user.force_password_change`` becomes true,
 * with no dependency on lazy chunks. The route-level guards
 * (``AppRequired`` etc.) keep their force-password-change checks so
 * direct URL hits still get redirected when the gate hasn't yet
 * mounted (e.g. cold-load on a protected URL).
 *
 * MUST be rendered INSIDE ``<BrowserRouter>`` so ``useNavigate`` /
 * ``useLocation`` work, but OUTSIDE ``<Suspense>`` so the effect is
 * not deferred while a lazy chunk loads.
 */
export default function ForcePasswordChangeGate() {
  const { forcePasswordChange, loading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  useEffect(() => {
    if (loading) return
    if (!forcePasswordChange) return
    if (location.pathname === '/change-password') return
    navigate('/change-password', { replace: true })
  }, [forcePasswordChange, loading, location.pathname, navigate])
  return null
}
